/**
 * Vue Router 3.x rejects the navigation promise when a guard redirects, aborts,
 * cancels or duplicates a navigation. Most `$router.push()` calls in this app
 * don't chain a `.catch()`, so those rejections reach `window.onunhandledrejection`
 * and Sentry reports them as unhandled errors. They are normal SPA behaviour, not bugs.
 *
 * `createRouterError()` (vue-router.esm.js:2045) tags every NavigationFailure with
 * `_isRouter`, which is the exact discriminator between an interrupted navigation and
 * a real error: a failed route chunk or an exception thrown inside a guard carries no
 * such flag and keeps reaching Sentry.
 */
const ROUTER_NOISE_PATTERNS = [
  /Redirected when going from/,
  /Avoided redundant navigation to current location/,
  /Navigation cancelled from/,
  /Navigation aborted from/,
];

/**
 * @param {{ originalException?: unknown }} [hint] hint Sentry passes to `beforeSend`
 * @returns {boolean} true when the event is vue-router navigation noise and must be dropped
 */
export const isRouterNavigationNoise = (hint) => {
  const error = hint?.originalException;
  if (!error || typeof error !== "object") return false;
  if (error._isRouter === true) return true;

  const message = typeof error.message === "string" ? error.message : "";
  return ROUTER_NOISE_PATTERNS.some((pattern) => pattern.test(message));
};

/**
 * `beforeSend` callback for `Sentry.init`: returning null drops the event.
 *
 * @param {object} event Sentry event about to be sent
 * @param {{ originalException?: unknown }} [hint] hint Sentry passes alongside the event
 * @returns {object|null} the event, or null when it is vue-router navigation noise
 */
export const dropRouterNavigationNoise = (event, hint) =>
  isRouterNavigationNoise(hint) ? null : event;
